import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Preloader, SimpleHeader, SimpleTable, useColumnsVisibility } from '@shared/UI';
import { Paginator } from '../Pagination';
import { TableProps } from './interfaces';
import classNames from 'classnames';
import { useKeyDown } from '@shared/react';
import { ColumnsControl } from './components';
import './Table.scss';
import { CircularProgress, Snackbar, SnackbarContent } from '@mui/material';
import useDisableStore from '@widgets/UserManagementTable/lib/store/disable-button-store';
import useUpdatePermissionStore from '@widgets/UserManagementTable/lib/store/update-permission-store';
import { putMethods } from '@widgets/ClientDetails/api';
import { FaRegSave } from 'react-icons/fa';
import useStore from '@widgets/UserManagementTable/lib/store/assign-client-store';
import ResetPermissions from '@widgets/UserManagementTable/lib/store/handle-reset-permission-store';

export const Table: React.FC<TableProps> = (props) => {
    const {
        tableTitle,
        showButton,
        infoLink,
        filters,
        paginationOptions,
        pageSizes = [10, 20, 30, 40, 50],
        data,
        columns,
        dataLoading,
        onAdd,
        row,
        summary,
        expandable = false,
        responsive = false,
        showColumnsControl = false,
        noScroll = false,
        noBorderTable = false,
    } = props;

    const [expanded, setExpanded] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const clientAssigned = useDisableStore();
    const updatedPermission = useUpdatePermissionStore();
    const setUpdatedPermissions = useUpdatePermissionStore((state) => state.setUpdatedPermission);
    const setDisableButton = useDisableStore((state) => state.setDisableButton);
    const setClientAssigned = useStore((state) => state.setClientAssigned);
    const setResetPermissions = ResetPermissions((state) => state.setResetPermissions);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const handleUpdatePermissions = async () => {
        setIsLoading(true);
        try {
            await putMethods(updatedPermission.updatedPermission);
            setUpdatedPermissions([]); // Clear the updated rows after successful updates
            setDisableButton(true);
            setClientAssigned(true);
            setSnackbarMessage('Permissions updated successfully.');
        } catch (error) {
            setSnackbarMessage('Failed to update permissions.');
        } finally {
            setIsLoading(false);
            setSnackbarOpen(true); // Show snackbar
        }
    };

    const handleResetPermissions = () => {
        setResetPermissions(true);
    };

    const expandClick = useCallback(() => {
        setExpanded((prev) => !prev);
    }, []);

    useKeyDown({
        onDown: useCallback(() => setExpanded(false), []),
        keyName: 'Escape',
    });

    const { columnsVisibleState, setColumnsVisibleState } = useColumnsVisibility({ columns, showColumnsControl });

    const table = useMemo(() => {
        if (responsive) {
            return (
                <div className='feature-table__scroll-wrapper'>
                    <SimpleTable
                        data={data}
                        columns={columns}
                        row={row}
                        className='feature-table__table responsible'
                        columnsVisibleState={columnsVisibleState}
                        summary={summary}
                    />
                </div>
            );
        }

        if (noBorderTable) {
            return (
                <SimpleTable
                    data={data}
                    columns={columns}
                    row={row}
                    className='feature-table__table no-border'
                    columnsVisibleState={columnsVisibleState}
                />
            );
        }

        return (
            <SimpleTable
                data={data}
                columns={columns}
                row={row}
                className='feature-table__table'
                columnsVisibleState={columnsVisibleState}
                tableToShow={props.tableToShow}
                summary={summary}
            />
        );
    }, [data, columns, responsive, columnsVisibleState]);

    return (
        <Card
            className={classNames(
                'feature-table',
                {
                    expanded: expandable && expanded,
                },
                { noScroll: noScroll && tableTitle !== 'Client level Securities' },
                { onlyBottom: tableTitle === 'Client level Securities' },
            )}
        >
            <Preloader
                loading={!!dataLoading}
                contentWrapperClassName={classNames('feature-table__content', {
                    expanded: expandable && expanded,
                })}
            >
                <div>
                    <div className='flex-only'>
                        {(tableTitle || expandable || onAdd || filters) && (
                            <SimpleHeader
                                headerTitle={tableTitle}
                                infoLink={infoLink}
                                className='feature-table__header'
                                onExpand={expandable ? expandClick : undefined}
                                expanded={expanded}
                                onAdd={onAdd}
                            >
                                {filters}
                            </SimpleHeader>
                        )}

                        {clientAssigned.disableButton && showButton && (
                            <Button
                                onClick={handleUpdatePermissions}
                                color='primary'
                                disabled={
                                    isLoading ||
                                    updatedPermission.updatedPermission.length === 0 ||
                                    updatedPermission.updatedPermission === data
                                }
                                className='update-permission-button'
                            >
                                <FaRegSave className='reg-save' />
                                {isLoading ? <CircularProgress size={24} /> : 'Update Permissions'}
                            </Button>
                        )}

                        {updatedPermission.updatedPermission.length !== 0 && showButton && (
                            <Button
                                onClick={handleResetPermissions}
                                color='primary'
                                disabled={
                                    isLoading ||
                                    updatedPermission.updatedPermission.length === 0 ||
                                    updatedPermission.updatedPermission === data
                                }
                                className='reset-permission-button'
                            >
                                {isLoading ? <CircularProgress size={24} /> : 'Reset Permissions'}
                            </Button>
                        )}
                    </div>
                    {table}
                </div>
                <div className='feature-table__footer'>
                    {columnsVisibleState && (
                        <ColumnsControl
                            columnsVisibleState={columnsVisibleState}
                            setColumnsVisibleState={setColumnsVisibleState}
                            className='feature-table__columns-control'
                        />
                    )}
                    {paginationOptions && (
                        <Paginator
                            {...paginationOptions}
                            pageSizes={pageSizes}
                        />
                    )}
                </div>
            </Preloader>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000} // Hide snackbar after 6 seconds
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <SnackbarContent
                    style={{
                        backgroundColor: snackbarMessage.includes('Failed') ? 'red' : 'rgb(162 237 164)',
                        color: 'white',
                    }}
                    message={snackbarMessage}
                />
            </Snackbar>
        </Card>
    );
};
